import { favorEmoji, giftFavorDelta, giftGpCost as tradeGiftGpCostForNextPoint, pricingFavor, favorTier } from '../janus-engine/inventory/trade-exchange';
import { COIN_GP } from '../janus-engine/inventory/unit-gp';
import type { ExchangePanelFacts, ExchangeSubmit } from '../janus-engine/inventory/trade-exchange';
import type { ExchangeAnswer } from '../janus-engine/inventory/trade-adjust';
import { actorCanReachTarget, getProximityEntity } from '../shared/proximity-browser';
import { exchangeActorPermitted, exchangeOutcomeUnknown, favorPrices } from '../janus-engine/inventory/trade-adjust';

// Merchant/trainer panel: stock presentation and staged offers. Browser exchange plans own decisions and dice;
// websocket exposes only the ftExchangePanelFacts / ftSubmitExchange bridge across the bundle boundary.

type TradeSide = 'player' | 'merchant';
type TradeEntry = {
	key: string;
	inventoryId: string;
	qty: number;
	unitGp: number;
	isCoin: boolean;
	name?: string;
	tradeOwnerEcId?: string;
	thumbUrl?: string;
	item_id?: string;
	sizeVar?: string;
	sizeId?: string;
	itemType?: string;
	enchantmentTemplateId?: string;
	equipmentSlot?: string;
};
type CoinStack = {
	inventory_id: string;
	item_id: string;
	qty: number;
	name?: string;
	thumb_url?: string;
	thumbUrl?: string;
};

type TradeState = {
	active: boolean;
	merchantEcId: string;
	playerEcId: string;
	playerOffer: TradeEntry[];
	merchantOffer: TradeEntry[];
	coinStacksAvailable: CoinStack[];
	merchantCoinStacksAvailable: CoinStack[];
	coinStacksInitialSnapshot: CoinStack[];
	merchantCoinStacksInitialSnapshot: CoinStack[];
	favor: number;
	merchantFavor: number | null;
	economy: unknown;
	buyP: number;
	sellP: number;
	hasHaggled: boolean;
	tierDescription: string;
	merchantChangeShortCu: number;
};

type TrainState = {
	active: boolean;
	trainerEcId: string;
	playerEcId: string;
	playerOffer: TradeEntry[];
	coinStacksAvailable: CoinStack[];
	coinStacksInitialSnapshot: CoinStack[];
	favor: number;
	merchantFavor: number | null;
	economy: unknown;
	sellP: number;
	tierDescription: string;
	selectedSkillVar: string;
	trainingCostGp: number;
	skillVerdicts: ExchangePanelFacts['training'];
};

type SideInfoPartnerContext = {
	partnerEcId: string;
	playerEcId: string;
	hasMerchant: boolean;
	hasTrainer: boolean;
};

declare global {
	interface Window {
        ftExchangePanelFacts: (input: { encounterId: string; playerId: string; counterpartId: string }) => Promise<ExchangePanelFacts>;
        ftExchangePanelGate: (input: { encounterId: string; playerId: string; counterpartId: string; training: boolean }) => Promise<string | null>;
        ftSubmitExchange: (input: ExchangeSubmit) => Promise<ExchangeAnswer>;
		ftPaintTradeStock?: (casterId: string, mount: HTMLElement) => Promise<void>;
		tradeState: TradeState | null;
		trainState: TrainState | null;
		__sideinfoPartnerContext?: SideInfoPartnerContext | null;
		bootstrap?: FtTradeTrainerBootstrap;
		SIZE_TYPES: ReadonlyArray<{ variable_name: string; item_id: string }>;
		FateTide?: { casterCache?: Map<string, unknown> };
		ftGetEncounterContext?: () => FtSidelogContext | null;
		ftGetCampaignId?: () => unknown;
		ftGetActiveEncounterId?: () => unknown;
		ftGetActiveCasterId?: () => unknown;
		ftLayerValue?: (arr: unknown, layerName: string) => unknown;
		enterTradeMode: typeof enterTradeMode;
		exitTradeMode: typeof exitTradeMode;
		tradeMerchantPortraitClick: typeof tradeMerchantPortraitClick;
		balanceTradeWithCoins: typeof balanceTradeWithCoins;
		calculateTradeBalance: typeof calculateTradeBalance;
		tradeClearAllOffers: typeof tradeClearAllOffers;
		tradeAddWaresToOffer: typeof tradeAddWaresToOffer;
		acceptTrade: typeof acceptTrade;
		haggleMerchant: typeof haggleMerchant;
		tradeDropzoneSetDragHint: typeof tradeDropzoneSetDragHint;
		tradeDropzoneClearDragHint: typeof tradeDropzoneClearDragHint;
		tradeCommitDropFromDrag: typeof tradeCommitDropFromDrag;
		enterTrainMode: typeof enterTrainMode;
		exitTrainMode: typeof exitTrainMode;
		trainOnSkillSelectChange: typeof trainOnSkillSelectChange;
		trainClearOffers: typeof trainClearOffers;
		balanceTrainWithCoins: typeof balanceTrainWithCoins;
		acceptTrainAndBegin: typeof acceptTrainAndBegin;
		trainTrainerPortraitClick: typeof trainTrainerPortraitClick;
		trainCommitDropFromDrag: typeof trainCommitDropFromDrag;
		ftGmOpenTrade: typeof ftGmOpenTrade;
		ftGmOpenTrain: typeof ftGmOpenTrain;
		// Live-stock glue called by the websocket bundle; optional there so a page without
		// this bundle simply never refreshes (see LIVE STOCK REFRESH below).
		ftExchangeOnParticipantUpdate?: typeof ftExchangeOnParticipantUpdate;
        ftExchangeFavorChanged?: () => void;
	}
}

window.tradeState = window.tradeState || null;

function tradeTrainerContext() {
	return window.ftGetEncounterContext?.()
		|| (typeof getSidelogContext === 'function' ? getSidelogContext() : null)
		|| {};
}

function tradeTrainerCampaignId() {
	const ctx = tradeTrainerContext();
	return String(ctx.campaign_id || window.ftGetCampaignId?.() || '');
}

function tradeTrainerEncounterId() {
	const ctx = tradeTrainerContext();
	return String(ctx.encounter_id || window.ftGetActiveEncounterId?.() || '');
}

function tradeTrainerActiveCasterId(): string {
	return String(window.ftGetActiveCasterId?.() || '').trim();
}

function tradeIsCoinItemId(id: unknown) {
	return typeof id === 'string' && COIN_GP[id.toUpperCase()] !== undefined;
}

/** true = open, false = refused with the loader's words, null = the participant read failed (undecided). */
async function exchangeParticipantGate(playerId: string, counterpartId: string, training: boolean, announceErrors = true): Promise<boolean | null> {
	try {
		const reason = await window.ftExchangePanelGate({ encounterId: tradeTrainerEncounterId(), playerId, counterpartId, training });
		if (!reason) return true;
		displayWarning(reason);
		return false;
	} catch (error) {
		if (announceErrors) displayWarning(exchangeDecisionError(error));
		else ftDebugLog('trade', () => ({ warn: 'exchange participant gate failed', err: String(error) }));
		return null;
	}
}

/** The exchange envelope's authority rule on the page (owner / campaign GM / STAFF — D-027): refuse without a request only when the page
 * proves the viewer may not act as this character; an unknown owner is left to the server. */
async function exchangeAuthorityGate(playerEcId: string): Promise<boolean> {
	const user = (window as unknown as { FateTide?: { user?: { id?: unknown; primaryRole?: unknown; isGM?: unknown } } }).FateTide?.user;
	let owner: string | null = null;
	try {
		const row = await getProximityEntity(playerEcId) as { encounter_creatures?: { encounter_creature_created_by_id?: unknown } } | null;
		const raw = row?.encounter_creatures?.encounter_creature_created_by_id;
		owner = String((Array.isArray(raw) ? raw[0] : raw) ?? '').trim() || null;
	} catch { owner = null; }
	const permitted = exchangeActorPermitted({ userId: String(user?.id ?? ''), primaryRole: String(user?.primaryRole ?? ''),
		isCampaignGm: user?.isGM === true, actorOwnerId: owner });
	if (permitted !== false) return true;
	displayWarning('You do not control that character.');
	return false;
}

async function exchangeReachGate(playerEcId: string, partnerEcId: string): Promise<boolean> {
	if (!await exchangeAuthorityGate(playerEcId)) return false;
	const within = await actorCanReachTarget(playerEcId, partnerEcId);
	if (within === true) return true;
	displayWarning(within === false ? 'You need to be within reach.' : 'Could not determine reach. Refresh the encounter and try again.');
	return false;
}

/** Relay the page's computed presentation verbatim; saved rows still establish PHP identity/authority. */
async function exchangeParticipantPresentations(playerId: string, counterpartId: string) {
	const [player, counterpart] = await Promise.allSettled([getProximityEntity(playerId), getProximityEntity(counterpartId)]);
	// standard_inputs' plaintext sanitizer must not rewrite URLs, names, booleans or lean nulls.
	const encode = (result: PromiseSettledResult<unknown>) => {
		if (result.status !== 'fulfilled' || !result.value) return null;
		const bytes = new TextEncoder().encode(JSON.stringify(result.value));
		let binary = '';
		for (let offset = 0; offset < bytes.length; offset += 32768) binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
		return btoa(binary);
	};
	return {
		player_presentation: encode(player),
		counterpart_presentation: encode(counterpart),
	};
}

async function enterTradeMode(merchantEcId: string, playerEcId: string, partnerAlsoTrainer?: boolean) {
	if (!merchantEcId || !playerEcId) {
		displayWarning('Missing trade participants.');
		return;
	}
	if (!await exchangeParticipantGate(playerEcId, merchantEcId, false)) {
		exchangeInvalidateSession(); window.tradeState = null;
		return;
	}
	if (!await exchangeReachGate(playerEcId, merchantEcId)) return;
	if (window.trainState && window.trainState.active && typeof exitTrainMode === 'function') {
		exitTrainMode({ restoreTrainer: false });
	}
	// New panel session — any refresh queued/in-flight for a previous partner must not commit.
	exchangeInvalidateSession();
	window.tradeState = null;
	window.__sideinfoPartnerContext = {
		partnerEcId: merchantEcId,
		playerEcId: playerEcId,
		hasMerchant: true,
		hasTrainer: !!partnerAlsoTrainer,
	};
	if (typeof forceOpenSideInfoPanel === 'function') {
		forceOpenSideInfoPanel();
	}
	document.body.classList.add('trade-mode-active');
	tradeTryActivatePlayerInventoryTab(playerEcId);

	const campaign_id = tradeTrainerCampaignId();
	const encounter_id = tradeTrainerEncounterId();
	const caster_id = tradeTrainerActiveCasterId();

	clearSideInfoMerchantPanelContent();
	await queryData(
		BASE_URL + 'options/tools/inventory/trade/load_trade_panel_inc.php',
		'div_sideinfo_merchant_panel',
		{
			...await exchangeParticipantPresentations(playerEcId, merchantEcId),
			campaign_id,
			encounter_id,
			caster_id,
			merchant_ec_id: merchantEcId,
			player_ec_id: playerEcId,
		}
	);

	if (!document.getElementById('div_merchant_trade_panel')) {
		document.body.classList.remove('trade-mode-active');
		displayWarning('Could not open trade panel.');
		clearSideInfoMerchantPanelContent();
		sideInfoHideExchangeTabUi();
		sideInfoActivateItemTab();
		window.__sideinfoPartnerContext = null;
		if (merchantEcId && typeof openItemInfo === 'function') {
			openItemInfo(merchantEcId, false);
		}
		return;
	}

	sideInfoShowExchangeTabUi('trade');
	sideInfoActivateExchangeTab();
	await tradeInitStateAfterPanel(merchantEcId, playerEcId);
	if (typeof scheduleSideinfoExchangeReachCheck === 'function') {
		scheduleSideinfoExchangeReachCheck();
	}
}

function tradeTryActivatePlayerInventoryTab(playerEcId: string) {
	const invEl = document.getElementById('inventory_' + playerEcId);
	if (!invEl) return;
	const pane = invEl.closest('.tab-pane');
	if (!pane || !pane.id) return;
	const trigger = document.querySelector('[data-bs-target="#' + CSS.escape(pane.id) + '"]');
	if (trigger && window.bootstrap && bootstrap.Tab) {
		bootstrap.Tab.getOrCreateInstance(trigger).show();
	}
}

// =============================================================================
// TRADE SUBSYSTEM (merchant trade UI)
// State init, render-one-side, coin balancing, offer merging, panel sync.
// Has a sibling block below ("TRAINER SUBSYSTEM") with significant overlap —
// see plan item A for a future PairExchangeWidget consolidation.
// =============================================================================
async function exchangeLoadPanelFacts(panelId: string, playerId: string, counterpartId: string): Promise<ExchangePanelFacts | null> {
	const token = exchangeSessionToken;
	try {
		const facts = await window.ftExchangePanelFacts({ encounterId: tradeTrainerEncounterId(), playerId, counterpartId });
		if (token !== exchangeSessionToken) return null;
		document.getElementById(panelId)?.querySelector('[data-exchange-state-error]')?.remove();
		return facts;
	} catch (error) {
		if (token !== exchangeSessionToken) return null;
		exchangePanelError(panelId, error);
		return null;
	}
}

function exchangePanelError(panelId: string, error: unknown): void {
	const panel = document.getElementById(panelId);
	if (!panel) return;
	const alert = panel.querySelector<HTMLElement>('[data-exchange-state-error]') ?? document.createElement('div');
	alert.setAttribute('data-exchange-state-error', '');
	alert.setAttribute('role', 'alert');
	alert.className = 'alert alert-warning';
	alert.textContent = `Could not load exchange state: ${exchangeDecisionError(error)} Reopen this panel to try again.`;
	panel.prepend(alert);
}

async function tradeInitStateAfterPanel(merchantEcId: string, playerEcId: string, beforeReset?: () => void): Promise<boolean> {
	const token = exchangeSessionToken;
	const favorPayload = await exchangeLoadPanelFacts('div_merchant_trade_panel', playerEcId, merchantEcId);
	if (!favorPayload) return false;
	try {
		const mount = document.getElementById('div_merchant_trade_panel')?.querySelector<HTMLElement>('[data-ft-trade-stock]');
		if (!mount) throw new Error('unported:merchant-stock:mount-not-held');
		if (!window.ftPaintTradeStock) throw new Error('unported:merchant-stock:painter-not-held');
		// Facts have adopted/compiled both participants through the existing C5 runtime.
		// Paint those same rows before availability or prices can reconcile a staged line.
		await window.ftPaintTradeStock(merchantEcId, mount);
	} catch (error) {
		if (token === exchangeSessionToken) exchangePanelError('div_merchant_trade_panel', error);
		return false;
	}
	if (token !== exchangeSessionToken) return false;
	beforeReset?.();
	const playerCoinPool = favorPayload.playerCoins;
	const merchantCoinPool = favorPayload.counterpartCoins;
	window.tradeState = {
		active: true,
		merchantEcId,
		playerEcId,
		playerOffer: [],
		merchantOffer: [],
		coinStacksAvailable: playerCoinPool.map((c) => ({ ...c })),
		merchantCoinStacksAvailable: merchantCoinPool.map((c) => ({ ...c })),
		coinStacksInitialSnapshot: playerCoinPool.map((c) => ({ ...c })),
		merchantCoinStacksInitialSnapshot: merchantCoinPool.map((c) => ({ ...c })),
		favor: favorPayload.favor,
        merchantFavor: favorPayload.rawFavor, economy: favorPayload.economy,
		buyP: favorPayload.buyP,
		sellP: favorPayload.sellP,
		hasHaggled: favorPayload.hasHaggled,
		tierDescription: favorPayload.tierDescription,
		merchantChangeShortCu: 0,
	};
	tradeApplyFavorUi();
	tradeRenderAllStaged();
	calculateTradeBalance();

	if (typeof refreshDragAndDrop === 'function') {
		refreshDragAndDrop();
	}
	if (typeof fateTideRefreshInventoryUnequippedSearch === 'function') {
		fateTideRefreshInventoryUnequippedSearch();
	}
	const tradeStockHost = document.getElementById('div_merchant_trade_panel');
	if (
		tradeStockHost
		&& typeof fateTideScrubInventoryUnequippedSearchInputsInContainer === 'function'
		&& typeof fateTideRefreshInventoryUnequippedSearch === 'function'
	) {
		fateTideScrubInventoryUnequippedSearchInputsInContainer(tradeStockHost);
		fateTideRefreshInventoryUnequippedSearch();
	}
	return true;
}

/**
 * @param {{ restoreMerchant?: boolean }} [options] - If restoreMerchant is false (e.g. after accept), caller refreshes the sheet.
 */
function exitTradeMode(options?: { restoreMerchant?: boolean }) {
	const restoreMerchant = !options || options.restoreMerchant !== false;
	const merchantEcId = window.tradeState?.merchantEcId;
	exchangeInvalidateSession();
	document.body.classList.remove('trade-mode-active');
	window.tradeState = null;
	clearSideInfoMerchantPanelContent();
	sideInfoHideExchangeTabUi();
	sideInfoActivateItemTab();
	window.__sideinfoPartnerContext = null;
	if (restoreMerchant && merchantEcId && typeof openItemInfo === 'function') {
		openItemInfo(merchantEcId, false);
	}
}

// =============================================================================
// LIVE STOCK REFRESH (websocket-driven)
// -----------------------------------------------------------------------------
// Reload stock markup and independently refresh browser exchange facts on participant deltas.
// Both pools and prices must update alongside stock to preserve a useful staged draft.
//
// The staged draft is preserved across the reload and reconciled against the
// fresh stock (see tradeReconcileOffer): a line whose stack was bought out from
// under the user is DROPPED and one that shrank is CLAMPED, both with a visible
// warning before the browser plans another exchange.
// =============================================================================

// Trailing debounce, plus a HARD CEILING on how long that trailing edge may be pushed
// out. Without the ceiling a steady stream of combat pushes (each one re-arming the
// timer) starves the refresh indefinitely — the panel would stay stale for exactly as
// long as the fight lasts, which is when trading matters least but staleness lasts longest.
const EXCHANGE_REFRESH_DEBOUNCE_MS = 220;
const EXCHANGE_REFRESH_MAX_WAIT_MS = 1500;

let exchangeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let exchangeRefreshInFlight = false;
let exchangeRefreshDirty = false;
// Wall-clock of the first coalesced request in the current burst (0 = no burst pending).
let exchangeRefreshFirstRequestAt = 0;
// Bumped on every enter/exit of either panel. A refresh that started under an old
// token must not commit its response over a panel the user has since closed or
// re-pointed at a different partner.
let exchangeSessionToken = 0;

function exchangeInvalidateSession(): void {
	exchangeSessionToken++;
	if (exchangeRefreshTimer) {
		clearTimeout(exchangeRefreshTimer);
		exchangeRefreshTimer = null;
	}
	exchangeRefreshDirty = false;
	exchangeRefreshFirstRequestAt = 0;
}

/**
 * Does `casterId` participate in the open exchange panel? Both participants count:
 * a merchant-only predicate would miss the player's own coin pool changing (a GM
 * move, another tab), which desyncs the balance calculation just as badly.
 */
function exchangeParticipantKind(casterId: string): 'trade' | 'train' | null {
	const id = String(casterId || '').toUpperCase();
	if (!id) return null;

	const tSt = window.tradeState;
	if (tSt?.active
		&& (id === String(tSt.merchantEcId).toUpperCase() || id === String(tSt.playerEcId).toUpperCase())
	) {
		return 'trade';
	}

	const nSt = window.trainState;
	if (nSt?.active
		&& (id === String(nSt.trainerEcId).toUpperCase() || id === String(nSt.playerEcId).toUpperCase())
	) {
		return 'train';
	}

	return null;
}

/**
 * Optional-global entry point the websocket bundle calls after it has committed a
 * caster/encounter update to the cache. Cross-bundle glue follows the established
 * `window.ft*?.()` pattern (ftRerenderOpenSideInfo, ftRerenderPcSheet, …) so the
 * websocket bundle stays ignorant of trade: it reports "this creature changed",
 * and ALL refresh policy lives here.
 *
 * `relevant` lets a delta-aware caller say the change CANNOT affect this panel — an
 * HP/status tick on a participant mid-combat would otherwise trigger a full
 * fragment reload for nothing. Omitted means
 * "unknown", which conservatively refreshes: a whole-row push carries no path info.
 *
 * Cheap no-op (one string compare) when no panel is open — it sits on the combat
 * hot path, so it must stay that way.
 */
function ftExchangeOnParticipantUpdate(casterId: string, relevant?: boolean): void {
	if (relevant === false) return;
	if (!exchangeParticipantKind(casterId)) return;
	scheduleExchangeStockRefresh();
}

/**
 * Trailing debounce + max-wait ceiling + single-flight + dirty follow-up. Deltas
 * arrive in bursts (both trade participants are patched by the same accept_trade
 * fan-out), so a naive per-message reload would be a fetch storm.
 */
function scheduleExchangeStockRefresh(): void {
	if (exchangeRefreshInFlight) {
		exchangeRefreshDirty = true;
		return;
	}

	const now = Date.now();
	if (!exchangeRefreshFirstRequestAt) exchangeRefreshFirstRequestAt = now;

	// Never push the trailing edge past the ceiling measured from the FIRST request
	// in this burst — see EXCHANGE_REFRESH_MAX_WAIT_MS.
	const elapsed = now - exchangeRefreshFirstRequestAt;
	const delay = Math.max(0, Math.min(EXCHANGE_REFRESH_DEBOUNCE_MS, EXCHANGE_REFRESH_MAX_WAIT_MS - elapsed));

	if (exchangeRefreshTimer) clearTimeout(exchangeRefreshTimer);
	exchangeRefreshTimer = setTimeout(() => {
		exchangeRefreshTimer = null;
		exchangeRefreshFirstRequestAt = 0;
		void drainExchangeStockRefresh();
	}, delay);
}

async function drainExchangeStockRefresh(): Promise<void> {
	if (exchangeRefreshInFlight) {
		exchangeRefreshDirty = true;
		return;
	}
	exchangeRefreshInFlight = true;
	try {
		if (window.tradeState?.active) {
			await refreshTradePanelPreservingDraft();
		} else if (window.trainState?.active) {
			await refreshTrainPanelPreservingDraft();
		}
	} catch (e) {
		// Retain the draft; submitting it loads fresh participants and runs the browser rules again.
		ftDebugLog('trade', () => ({ warn: 'exchange refresh failed', err: String(e) }));
	} finally {
		exchangeRefreshInFlight = false;
		if (exchangeRefreshDirty) {
			exchangeRefreshDirty = false;
			scheduleExchangeStockRefresh();
		}
	}
}

/**
 * Fetch a panel fragment WITHOUT committing it anywhere. Returns null on a network or
 * HTTP failure.
 *
 * Deliberately not queryData(): that writes the response straight into the target
 * container before the caller can validate it (so a response for a panel the user has
 * since closed, or re-pointed at another merchant, would paint over the new one) AND it
 * swallows network errors, which would leave us "reinitializing" from the OLD DOM as if
 * a refresh had succeeded. Fetching first lets us validate the session, then commit.
 */
async function exchangeFetchPanelHtml(endpoint: string, data: Record<string, unknown>): Promise<string | null> {
	try {
		const gate = await exchangeParticipantGate(String(data.player_ec_id ?? ''), String(data.merchant_ec_id ?? data.trainer_ec_id ?? ''),
			data.trainer_ec_id !== undefined, false);
		// A failed participant read is a transient failure like a failed fetch: keep the last good paint and the draft.
		if (gate === null) return null;
		if (!gate) return '';
		const response = await fetch(BASE_URL + endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
		if (!response.ok) return null;
		return await response.text();
	} catch (e) {
		ftDebugLog('trade', () => ({ warn: 'exchange fragment fetch failed', err: String(e) }));
		return null;
	}
}

/**
 * Fresh availability for one staged line, read off the just-committed fragment.
 * Mirrors tradeCommitDropFromDrag's ownership check (data-trade_owner_ec_id) so a
 * line can never survive by matching a same-id stack on the wrong creature.
 *
 * `root` scopes the search: the partner's stock MUST be resolved inside the freshly
 * rendered panel, or a stale tile elsewhere in the document (the merchant's own sheet
 * open behind the drawer) could answer first and vouch for stock that is already sold.
 */
function exchangeFindStockEl(
	inventoryId: string,
	expectedOwnerEcId: string,
	root: ParentNode,
): HTMLElement | null {
	if (!inventoryId || !expectedOwnerEcId) return null;
	const els = root.querySelectorAll<HTMLElement>(
		`.inventory-item[data-inventory_id="${CSS.escape(inventoryId)}"]`,
	);
	for (let i = 0; i < els.length; i++) {
		const el = els[i]!;
		if ((el.dataset.trade_owner_ec_id || '') === expectedOwnerEcId) return el;
	}
	return null;
}

function exchangeStockQuantity(el: HTMLElement): number {
	return Math.max(0, parseInt(el.dataset.inventory_qty ?? '1', 10) || 0);
}

/**
 * Reconcile one side's staged draft against the reloaded stock, returning the
 * surviving entries plus human-readable notes about what changed.
 *
 * Quantity is budgeted PER inventory_id across the whole side, not per line: the same
 * stack can legitimately be staged as several lines, and both accept endpoints sum
 * duplicate lines before validating in the browser. Clamping each line
 * independently against the full remaining quantity would let 2 lines of 4 survive
 * against a stock of 5 — an offer of 8 that the browser then refuses.
 *
 * ASYMMETRY (deliberate): a MISSING tile drops the line only on the side whose column
 * the fragment just re-rendered (the partner's). The player's own tiles live in the
 * character sheet, which repaints on its own schedule — treating "not in the DOM yet"
 * as "sold" there would delete valid lines during a repaint race. Player lines
 * therefore clamp on positive evidence but are kept when no tile is found;
 * the browser planner reads current participants again before submitting.
 */
function tradeReconcileOffer(
	prev: TradeEntry[],
	ownerEcId: string,
	dropWhenMissing: boolean,
	root: ParentNode,
	notes: string[],
): TradeEntry[] {
	const kept: TradeEntry[] = [];
	const remainingByInventoryId = new Map<string, number>();

	for (const entry of prev) {
		// Coins reconcile against the freshly-parsed coin pools, not the stock DOM.
		if (entry.isCoin) {
			kept.push(entry);
			continue;
		}

		const el = exchangeFindStockEl(entry.inventoryId, ownerEcId, root);
		if (!el) {
			if (dropWhenMissing) {
				notes.push(`${entry.name || 'An item'} is no longer available and was removed from the offer.`);
				continue;
			}
			kept.push(entry);
			continue;
		}

		// First line to touch this stack seeds the budget; later lines draw from what it left.
		if (!remainingByInventoryId.has(entry.inventoryId)) {
			remainingByInventoryId.set(entry.inventoryId, exchangeStockQuantity(el));
		}
		const remaining = remainingByInventoryId.get(entry.inventoryId) ?? 0;

		if (remaining <= 0) {
			notes.push(`${entry.name || 'An item'} is no longer available and was removed from the offer.`);
			continue;
		}
		if (entry.qty > remaining) {
			notes.push(`${entry.name || 'An item'} dropped to ${remaining}; your offer was reduced.`);
			entry.qty = remaining;
		}
		remainingByInventoryId.set(entry.inventoryId, remaining - entry.qty);

		// Re-price from the browser-painted tile — a re-stocked item can carry a new cost_gp.
		const freshUnitGp = parseFloat(el.dataset.cost_gp ?? '');
		if (Number.isFinite(freshUnitGp)) entry.unitGp = freshUnitGp;

		kept.push(entry);
	}

	return kept;
}

/**
 * Re-stage one coin line onto the refreshed pool, clamped to the coins that actually
 * remain. Coins reconcile against the freshly-parsed pool rather than the stock DOM
 * (they are not rendered as stock tiles), and they CAN move without this panel
 * acting — the merchant paying another customer drains the same stacks.
 *
 * Returns false when the stack is gone entirely, so the caller drops the line.
 */
function tradeRestageCoinEntry(side: TradeSide, entry: TradeEntry, notes: string[]): boolean {
	const st = window.tradeState;
	if (!st) return false;

	const pool = side === 'merchant' ? st.merchantCoinStacksAvailable : st.coinStacksAvailable;
	const stack = (pool || []).find((c) => c.inventory_id === entry.inventoryId);
	const availableQty = stack ? (stack.qty || 0) : 0;

	if (availableQty <= 0) {
		notes.push(`${entry.name || 'Staged coins'} are no longer available and were removed from the offer.`);
		return false;
	}
	if (entry.qty > availableQty) {
		notes.push(`${entry.name || 'Staged coins'} dropped to ${availableQty}; your offer was reduced.`);
		entry.qty = availableQty;
	}

	tradeMergeCoinOfferEntry(side, entry);
	tradeApplyCoinPoolDelta(side, entry.inventoryId, -entry.qty);
	return true;
}

/** Search-box text + scroll offset the user had in the stock column, so a background
 * refresh doesn't silently reset what they were looking at. innerHTML necessarily
 * destroys the live nodes, so the values are read before the swap and restored after. */
type ExchangeViewState = { search: string; scrollTop: number; itemType: string };

function exchangeCaptureViewState(hostId: string): ExchangeViewState {
	const host = document.getElementById(hostId);
	const search = host?.querySelector<HTMLInputElement>('input.fate-tide-inventory-unequipped-search');
	const scroller = host?.querySelector<HTMLElement>('.trade-panel-merchant-stock');
	const root = host?.querySelector<HTMLElement>('.fate-tide-inventory-unequipped-search-root');
	return { search: search?.value || '', scrollTop: scroller?.scrollTop || 0,
		itemType: root?.getAttribute('data-ft-inventory-type-filter-active') || '' };
}

function exchangeRestoreViewState(hostId: string, view: ExchangeViewState): void {
	const host = document.getElementById(hostId);
	if (!host) return;
	const search = host.querySelector<HTMLInputElement>('input.fate-tide-inventory-unequipped-search');
	if (search) search.value = view.search;
	host.querySelector<HTMLElement>('.fate-tide-inventory-unequipped-search-root')
		?.setAttribute('data-ft-inventory-type-filter-active', view.itemType);
	if (typeof fateTideRefreshInventoryUnequippedSearch === 'function') fateTideRefreshInventoryUnequippedSearch();
	if (view.scrollTop) {
		const scroller = host.querySelector<HTMLElement>('.trade-panel-merchant-stock');
		if (scroller) scroller.scrollTop = view.scrollTop;
	}
}

/**
 * Reload the merchant fragment and re-install the staged draft on top of the
 * fresh economic state. Deliberately NOT enterTradeMode(): that clears the live
 * panel before fetching (a blank flash mid-negotiation) and its failure branch
 * assumes no prior tradeState.
 */
async function refreshTradePanelPreservingDraft(): Promise<void> {
	const st = window.tradeState;
	if (!st?.active) return;

	const token = exchangeSessionToken;
	const merchantEcId = st.merchantEcId;
	const playerEcId = st.playerEcId;
	const hostId = 'div_sideinfo_merchant_panel';

	const html = await exchangeFetchPanelHtml(
		'options/tools/inventory/trade/load_trade_panel_inc.php',
		{
			...await exchangeParticipantPresentations(playerEcId, merchantEcId),
			campaign_id: tradeTrainerCampaignId(),
			encounter_id: tradeTrainerEncounterId(),
			caster_id: tradeTrainerActiveCasterId(),
			merchant_ec_id: merchantEcId,
			player_ec_id: playerEcId,
		},
	);

	// Network/HTTP failure: keep the last good paint and the untouched draft rather than
	// reinitializing from stale DOM as if the refresh had succeeded.
	if (html === null) return;

	// The panel was closed / re-pointed while the request was in flight — its response
	// is about a trade that no longer exists. Checked BEFORE committing anything.
	if (token !== exchangeSessionToken || !window.tradeState?.active) return;

	// Capture the view before replacing its nodes. Draft edits survive until the later state reset.
	const view = exchangeCaptureViewState(hostId);

	const host = document.getElementById(hostId);
	if (!host) return;
	host.innerHTML = html;

	if (typeof loadPostFunctions === 'function') {
		await loadPostFunctions(false);
		// loadPostFunctions is async — re-validate before touching state.
		if (token !== exchangeSessionToken || !window.tradeState?.active) return;
	}

	// The browser gate returns no markup when the participants are no longer available;
	// the loader also retains its not-found response. Close the obsolete panel in either case.
	const panelRoot = document.getElementById('div_merchant_trade_panel');
	if (!panelRoot) {
		displayWarning('The trade is no longer available.');
		exitTradeMode({ restoreMerchant: false });
		return;
	}

	// Rebuild stock presentation and browser-decided exchange facts, then restore the reconciled draft.
	let prevPlayerOffer: TradeEntry[] = [], prevMerchantOffer: TradeEntry[] = [];
	if (!await tradeInitStateAfterPanel(merchantEcId, playerEcId, () => {
		// Snapshot immediately before reset: edits during fact hydration/painting also survive.
		prevPlayerOffer = window.tradeState!.playerOffer.map((o) => ({ ...o }));
		prevMerchantOffer = window.tradeState!.merchantOffer.map((o) => ({ ...o }));
	})) return;

	const fresh = window.tradeState;
	if (!fresh) return;

	const notes: string[] = [];
	// Player tiles live in the sheet (document scope); merchant stock must resolve inside
	// the freshly rendered panel — see exchangeFindStockEl.
	const keptPlayer = tradeReconcileOffer(prevPlayerOffer, playerEcId, false, document, notes);
	const keptMerchant = tradeReconcileOffer(prevMerchantOffer, merchantEcId, true, panelRoot, notes);

	// Coin lines re-enter through the merge/pool helpers so the available pools stay
	// consistent with what is staged; non-coin lines push straight on.
	for (const entry of keptPlayer) {
		if (entry.isCoin) {
			if (!tradeRestageCoinEntry('player', entry, notes)) continue;
		} else {
			fresh.playerOffer.push(entry);
		}
	}
	for (const entry of keptMerchant) {
		if (entry.isCoin) {
			if (!tradeRestageCoinEntry('merchant', entry, notes)) continue;
		} else {
			fresh.merchantOffer.push(entry);
		}
	}

	tradeRenderAllStaged();
	calculateTradeBalance();
	exchangeRestoreViewState(hostId, view);

	if (notes.length > 0) {
		displayWarning(`The trader's stock changed. ${notes.join(' ')}`);
	}
}

/** Trainer twin — same one-shot-fragment bug, same fix. The trainer has a single
 * (player) offer, and its skill selection must survive the reload. */
async function refreshTrainPanelPreservingDraft(): Promise<void> {
	const st = window.trainState;
	if (!st?.active) return;

	const token = exchangeSessionToken;
	const trainerEcId = st.trainerEcId;
	const playerEcId = st.playerEcId;
	const hostId = 'div_sideinfo_trainer_panel';

	const html = await exchangeFetchPanelHtml(
		'options/tools/inventory/trade/load_train_panel_inc.php',
		{
			...await exchangeParticipantPresentations(playerEcId, trainerEcId),
			campaign_id: tradeTrainerCampaignId(),
			encounter_id: tradeTrainerEncounterId(),
			caster_id: tradeTrainerActiveCasterId(),
			trainer_ec_id: trainerEcId,
			player_ec_id: playerEcId,
		},
	);

	if (html === null) return;
	if (token !== exchangeSessionToken || !window.trainState?.active) return;

	// Captured post-fetch so mid-flight edits survive — see refreshTradePanelPreservingDraft.
	const prevPlayerOffer = window.trainState.playerOffer.map((o) => ({ ...o }));
	const prevSkillVar = window.trainState.selectedSkillVar;
	const view = exchangeCaptureViewState(hostId);

	const host = document.getElementById(hostId);
	if (!host) return;
	host.innerHTML = html;

	if (typeof loadPostFunctions === 'function') {
		await loadPostFunctions(false);
		if (token !== exchangeSessionToken || !window.trainState?.active) return;
	}

	if (!document.getElementById('div_trainer_train_panel')) {
		displayWarning('The training session is no longer available.');
		exitTrainMode({ restoreTrainer: false });
		return;
	}

	await trainInitStateAfterPanel(trainerEcId, playerEcId);

	const fresh = window.trainState;
	if (!fresh) return;

	const notes: string[] = [];

	// Re-select the skill only if the refreshed trainer still offers it — the price
	// (trainingCostGp) comes from the refreshed browser verdict.
	if (prevSkillVar) {
		const sel = document.querySelector<HTMLSelectElement>('#train_skill_select');
		const stillOffered = !!sel?.querySelector(`option[value="${CSS.escape(prevSkillVar)}"]`);
		if (stillOffered && sel) {
			// Restore the selected skill; the handler uses the browser eligibility/cost verdict.
			sel.value = prevSkillVar;
			trainOnSkillSelectChange(prevSkillVar);
		} else {
			// Folded into `notes` rather than warned immediately — a second displayWarning
			// below would overwrite this one before the user could read it.
			notes.push('The trainer no longer teaches your selected skill.');
		}
	}

	for (const entry of tradeReconcileOffer(prevPlayerOffer, playerEcId, false, document, notes)) {
		if (entry.isCoin) {
			const stack = (fresh.coinStacksAvailable || []).find((c) => c.inventory_id === entry.inventoryId);
			const availableQty = stack ? (stack.qty || 0) : 0;
			if (availableQty <= 0) {
				notes.push(`${entry.name || 'Staged coins'} are no longer available and were removed from the offer.`);
				continue;
			}
			if (entry.qty > availableQty) {
				notes.push(`${entry.name || 'Staged coins'} dropped to ${availableQty}; your offer was reduced.`);
				entry.qty = availableQty;
			}
			tradeMergeCoinOfferEntryTrain(entry);
			trainApplyCoinPoolDelta(entry.inventoryId, -entry.qty);
		} else {
			fresh.playerOffer.push(entry);
		}
	}

	trainRenderStaged();
	trainCalculateBalance();
	exchangeRestoreViewState(hostId, view);

	if (notes.length > 0) displayWarning(notes.join(' '));
}

/**
 * While dragging an inventory line in active trade, tint the offer zone that will accept the item
 * (matches tradeCommitDropFromDrag: data-trade_owner_ec_id vs player/merchant ec).
 */
function tradeDropzoneSetDragHint(inventoryItemEl: HTMLElement) {
	if (!window.tradeState?.active || !inventoryItemEl?.classList?.contains('inventory-item')) return;
	const st = window.tradeState;
	const owner = (inventoryItemEl.dataset.trade_owner_ec_id || '').trim();
	if (!owner) return;
	const zPlayer = document.getElementById('trade_zone_player_offer');
	const zMerchant = document.getElementById('trade_zone_merchant_offer');
	if (!zPlayer || !zMerchant) return;
	if (owner === st.playerEcId) {
		zPlayer.classList.add('trade-offer-zone--drop-hint-player');
	} else if (owner === st.merchantEcId) {
		zMerchant.classList.add('trade-offer-zone--drop-hint-merchant');
	}
}

function tradeDropzoneClearDragHint() {
	document.getElementById('trade_zone_player_offer')?.classList.remove(
		'trade-offer-zone--drop-hint-player',
	);
	document.getElementById('trade_zone_merchant_offer')?.classList.remove(
		'trade-offer-zone--drop-hint-merchant',
	);
}

function tradeRandomKey() {
	return 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
}

/** Empty both offers and restore coin pools to panel-load snapshot (for “Clear all”). */
function tradeClearAllOffers() {
	const st = window.tradeState;
	if (!st || !st.active) return;
	st.playerOffer = [];
	st.merchantOffer = [];
	if (Array.isArray(st.coinStacksInitialSnapshot)) {
		st.coinStacksAvailable = JSON.parse(JSON.stringify(st.coinStacksInitialSnapshot));
	}
	if (Array.isArray(st.merchantCoinStacksInitialSnapshot)) {
		st.merchantCoinStacksAvailable = JSON.parse(JSON.stringify(st.merchantCoinStacksInitialSnapshot));
	}
	st.merchantChangeShortCu = 0;
	tradeRenderAllStaged();
	calculateTradeBalance();
}

/**
 * "Add wares to offer" (BG3 sell-all-wares): stage EVERY eligible wares-flagged item the player
 * owns into the player offer at full remaining quantity, then let the normal balance/accept flow
 * take over. Eligibility mirrors what accept_trade will tolerate:
 *   - player-owned tiles only (data-trade_owner_ec_id === playerEcId; equipped paper-doll tiles
 *     never carry that attr, so worn gear is excluded — accept_trade rejects it anyway)
 *   - coins skipped (currency is the balancing medium, not merchandise)
 *   - containers skipped (their tile price is the bag row alone, and staging a bag + its contents
 *     would re-root the contents mid-accept — the ancestor/descendant hazard; bag CONTENTS stage
 *     fine individually)
 *   - stacks already fully staged are skipped (dedup across sheet + fullscreen-window tile copies)
 */
function tradeAddWaresToOffer() {
	const st = window.tradeState;
	if (!st || !st.active) return;
	const seen = new Set<string>();
	let added = 0;
	let skippedContainers = 0;
	const tiles = Array.from(document.querySelectorAll<HTMLElement>(
		`.inventory-item[data-is_wares="1"][data-trade_owner_ec_id="${st.playerEcId}"]`,
	));
	for (const el of tiles) {
		const inventoryId = el.dataset.inventory_id || '';
		if (!inventoryId || seen.has(inventoryId)) continue;
		seen.add(inventoryId);
		if (tradeIsCoinItemId(el.dataset.item_id ?? '')) continue;
		if (el.dataset.dropzone_type === 'dropzone_container') { skippedContainers++; continue; }
		const maxAdd = tradeMaxAddQtyFromEl(el, 'player');
		if (maxAdd <= 0) continue;
		tradeAddStagedFromElement(el, 'player', maxAdd);
		added++;
	}
	if (!added) {
		displayWarning(skippedContainers
			? 'Only flagged containers found — add bags to the offer by dragging them (their contents sell separately).'
			: 'No wares to add — shift-click items in your inventory to mark them first.');
		return;
	}
	if (skippedContainers) {
		displayWarning(`Added ${added} wares. ${skippedContainers} flagged container(s) skipped — drag bags in individually.`);
	}
}

function tradeQtyAlreadyStaged(side: TradeSide, inventoryId: string): number {
	const st = window.tradeState;
	if (!st) return 0;
	const arr = side === 'player' ? st.playerOffer : st.merchantOffer;
	return arr.filter((o: TradeEntry) => o.inventoryId === inventoryId).reduce((s: number, o: TradeEntry) => s + o.qty, 0);
}

function tradeMaxAddQtyFromEl(el: HTMLElement, side: TradeSide): number {
	const cap = exchangeStockQuantity(el);
	const already = tradeQtyAlreadyStaged(side, el.dataset.inventory_id ?? '');
	return Math.max(0, cap - already);
}

function tradeCommitDropFromDrag(el: HTMLElement, tradeZone: HTMLElement) {
	if (!window.tradeState || !window.tradeState.active) return;
	const dtype = tradeZone.dataset.dropzone_type;
	const side = dtype === 'dropzone_trade_player_offer' ? 'player' : 'merchant';
	const expectedOwner = side === 'player' ? window.tradeState.playerEcId : window.tradeState.merchantEcId;
	// Thumbnail uses data-trade_owner_ec_id (underscores) → dataset.trade_owner_ec_id, not camelCase tradeOwnerEcId.
	const owner = el.dataset.trade_owner_ec_id || '';
	if (!owner || owner !== expectedOwner) {
		displayWarning('That item cannot go in this offer.');
		return;
	}
	// has_donning_doffing reflects item *definition* (armor may have don/doff times), not whether it is worn.
	// Loose bag items are fine; the browser exchange planner excludes equipped stacks.
	const maxAdd = tradeMaxAddQtyFromEl(el, side);
	if (maxAdd <= 0) {
		displayWarning('No remaining quantity of that stack to add.');
		return;
	}
	const id = el.dataset.inventory_id || '';
	if (!id) {
		displayWarning('That item has no inventory identifier.');
		return;
	}
	const fromId = el.dataset.move_from_inventory_id || null;
	const showLog = parseInt(el.dataset.show_log_message || '1', 10);

	if (maxAdd > 1) {
		showInventorySliderPopup(
			id,
			maxAdd,
			fromId,
			'',
			'move_inventory',
			showLog,
			false,
			null,
			null,
			0,
			0,
			0,
			(qty: number) => tradeAddStagedFromElement(el, side, qty)
		);
	} else {
		tradeAddStagedFromElement(el, side, 1);
	}
}

function tradeAddStagedFromElement(el: HTMLElement, side: TradeSide, qty: number) {
	if (!window.tradeState || !window.tradeState.active) return;
	const maxAdd = tradeMaxAddQtyFromEl(el, side);
	if (qty > maxAdd) qty = maxAdd;
	if (qty <= 0) return;
	window.tradeState.merchantChangeShortCu = 0;

	const inventoryId = el.dataset.inventory_id || '';
	if (!inventoryId) return;
	let unitGp = parseFloat(el.dataset.cost_gp ?? '');
	if (!Number.isFinite(unitGp)) unitGp = 0;
	const isCoin = tradeIsCoinItemId(el.dataset.item_id ?? '');
	const imgEl = el.querySelector<HTMLImageElement>('img.img-thumbnail, img.token-img, .d-flex img');
	const thumbUrl =
		imgEl && (imgEl.currentSrc || imgEl.src)
			? String(imgEl.currentSrc || imgEl.src).trim()
			: '';
	const itemId = el.dataset.item_id || '';
	const entry = {
		key: tradeRandomKey(),
		inventoryId,
		qty,
		unitGp,
		name: el.dataset.inventory_name || 'Item',
		tradeOwnerEcId: el.dataset.trade_owner_ec_id || '',
		isCoin,
		thumbUrl,
		item_id: itemId || undefined,
		sizeVar: el.dataset.size || 'tiny',
		itemType: el.dataset.item_type || '',
		enchantmentTemplateId: el.dataset.enchantment_template_id || '',
	};

	if (isCoin) {
		tradeMergeCoinOfferEntry(side, entry);
		tradeApplyCoinPoolDelta(side, inventoryId, -qty);
	} else if (side === 'player') {
		window.tradeState.playerOffer.push(entry);
	} else {
		window.tradeState.merchantOffer.push(entry);
	}

	tradeRenderAllStaged();
	calculateTradeBalance();
}

function tradeApplyCoinPoolDelta(side: TradeSide, inventoryId: string, deltaQty: number) {
	if (!window.tradeState) return;
	const pool =
		side === 'merchant'
			? window.tradeState.merchantCoinStacksAvailable
			: window.tradeState.coinStacksAvailable;
	if (!pool) return;
	for (const st of pool) {
		if (st.inventory_id === inventoryId) {
			st.qty = Math.max(0, (st.qty || 0) + deltaQty);
			break;
		}
	}
}

/** Same inventory stack → one offer line (accept_trade rejects duplicate inventory_id). */
function tradeMergeCoinOfferEntry(side: TradeSide, entry: TradeEntry) {
	if (!window.tradeState || !entry || !entry.isCoin) return;
	const arr = side === 'player' ? window.tradeState.playerOffer : window.tradeState.merchantOffer;
	const existing = arr.find((e: TradeEntry) => e.isCoin && e.inventoryId === entry.inventoryId);
	if (existing) {
		existing.qty += entry.qty;
		if (!existing.thumbUrl && entry.thumbUrl) {
			existing.thumbUrl = entry.thumbUrl;
		}
		if (existing.item_id == null && entry.item_id != null) {
			existing.item_id = entry.item_id;
		}
		return;
	}
	arr.push(entry);
}

function tradeLineTradeGp(entry: TradeEntry, side: TradeSide): number {
	const st = window.tradeState;
	if (!st || !entry) return 0;
	const mult = entry.isCoin ? 1 : side === 'player' ? st.sellP : st.buyP;
	return entry.unitGp * entry.qty * mult;
}

function tradeFavorDeltaFromOverpayGp(overpayGp: number): number {
    return window.tradeState ? giftFavorDelta(overpayGp, window.tradeState.merchantFavor) : 0;
}

function exchangeApplyFavorDisplay(id: string, favor: number, description: string) {
	const em = document.getElementById(id);
	if (!em) return;
	const tooltips = window.bootstrap?.Tooltip;
	try {
		const existing = tooltips?.getInstance(em);
		if (existing) ftDisposeBootstrapTooltipInstanceSafe(existing);
	} catch { /* leave the text usable if tooltip teardown fails */ }
	// Disposal restores the old title, so write the current facts only after teardown.
	em.textContent = favorEmoji(favor);
	em.setAttribute('title', description || '');
	em.setAttribute('data-bs-original-title', description || '');
	try { if (tooltips) new tooltips(em); } catch { /* text/title remain available */ }
}

function tradeApplyFavorUi() {
	const st = window.tradeState;
	if (!st || !st.active) return;
	exchangeApplyFavorDisplay('trade_merchant_favor_emoji', st.favor, st.tierDescription);
	const hb = document.querySelector<HTMLButtonElement>('#btn_trade_haggle');
	if (hb) {
		hb.disabled = st.hasHaggled;
		hb.setAttribute('aria-disabled', String(st.hasHaggled));
		hb.classList.toggle('disabled', st.hasHaggled);
		hb.title = st.hasHaggled ? 'Already haggled this merchant.' : 'Haggle (Influence vs Empathy, once per visit)';
	}
}

function tradeRenderAllStaged() {
	if (!window.tradeState) return;
	tradeRenderOneSide('player', 'trade_zone_player_offer');
	tradeRenderOneSide('merchant', 'trade_zone_merchant_offer');
}

function tradeInventorySizeIdFromVariableName(sizeVar: string): string {
	const found = window.SIZE_TYPES.find((t) => t.variable_name === (sizeVar || 'tiny'));
	return found?.item_id ?? window.SIZE_TYPES[0]?.item_id ?? '';
}

/** Minimal inventory_arr shape for renderInventoryThumbnail() in trade offer chips. */
function tradeEntryToInventoryLike(entry: TradeEntry) {
	const sizeId = entry.sizeId || tradeInventorySizeIdFromVariableName(entry.sizeVar || 'tiny');
	const inv: Record<string, unknown[]> = {
		inventory_id: [entry.inventoryId],
		inventory_name: [entry.name || 'Item'],
		item_qty: [entry.qty],
		item_type: [entry.itemType || ''],
		size: [sizeId],
	};
	if (entry.enchantmentTemplateId) {
		inv.enchantment_template_id = [entry.enchantmentTemplateId];
	}
	if (entry.equipmentSlot) {
		inv.equipment_slot = [entry.equipmentSlot];
	}
	return inv;
}

function tradeRenderOneSide(side: TradeSide, containerId: string) {
	const box = document.getElementById(containerId);
	const st = window.tradeState;
	if (!box || !st) return;
	const arr = side === 'player' ? st.playerOffer : st.merchantOffer;
	box.innerHTML = '';
	const thumbW = 72;
	for (const entry of arr) {
		const wrap = document.createElement('div');
		wrap.className = 'trade-staged-item';
		wrap.dataset.stagedKey = entry.key;
		const gp = tradeLineTradeGp(entry, side).toFixed(2);
		const inventoryLike = tradeEntryToInventoryLike(entry);
		let thumbHtml = '';
		try {
			thumbHtml = renderInventoryThumbnail(inventoryLike, {}, {
				show_overlay: false,
				use_size_based_width: false,
				thumbnail_width: thumbW,
				thumbnail_height: null,
				is_draggable: false,
				show_log_message: 0,
				show_icons: true,
				additional_classes: ['trade-staged-inventory-thumb'],
				dom_id: false,
				inline_layout: true,
				exclude_dropzone: true,
				always_show_quantity_badge: true,
				override_thumb_url: entry.thumbUrl || null,
				omit_tooltip: true,
			});
		} catch (e) {
			ftDebugLog('trade', () => ({ warn: 'trade staged thumbnail', err: String(e) }));
			thumbHtml =
				'<div class="small text-muted text-center p-1" style="width:' +
				thumbW +
				'px;">?</div>';
		}
		wrap.innerHTML =
			'<div class="trade-staged-thumb-stack position-relative d-inline-block">' +
			thumbHtml +
			'<button type="button" class="trade-remove-staged" aria-label="Remove from offer">' +
			'<span class="visually-hidden">Remove from offer</span>' +
			'<svg class="trade-remove-staged-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M3 3l6 6M9 3l-6 6"/></svg>' +
			'</button>' +
			'</div>' +
			'<div class="trade-staged-gp text-center"><span class="badge bg-dark border border-secondary text-light fw-normal">' +
			gp +
			' gp</span></div>';
		const btn = wrap.querySelector('.trade-remove-staged');
		btn?.addEventListener('click', (ev) => {
			ev.stopPropagation();
			tradeRemoveStaged(side, entry.key);
		});
		box.appendChild(wrap);
	}
}

function tradeRemoveStaged(side: TradeSide, key: string) {
	if (!window.tradeState) return;
	window.tradeState.merchantChangeShortCu = 0;
	const arr = side === 'player' ? window.tradeState.playerOffer : window.tradeState.merchantOffer;
	const idx = arr.findIndex((o: TradeEntry) => o.key === key);
	if (idx < 0) return;
	const removed = arr[idx];
	arr.splice(idx, 1);
	if (!removed) return;
	if (removed.isCoin) {
		tradeApplyCoinPoolDelta(side, removed.inventoryId, removed.qty);
	}
	tradeRenderAllStaged();
	calculateTradeBalance();
}

function calculateTradeBalance() {
	if (!window.tradeState || !window.tradeState.active) return;
	let p = 0;
	let m = 0;
	for (const o of window.tradeState.playerOffer) p += tradeLineTradeGp(o, 'player');
	for (const o of window.tradeState.merchantOffer) m += tradeLineTradeGp(o, 'merchant');
	p = Math.round(p * 100) / 100;
	m = Math.round(m * 100) / 100;
	const pe = document.getElementById('trade_val_player');
	const me = document.getElementById('trade_val_merchant');
	const dl = document.getElementById('trade_deficit_label');
	const favorMsg = document.getElementById('trade_favor_msg');
	const accept = document.querySelector<HTMLButtonElement>('#btn_trade_accept');
	if (pe) pe.textContent = p.toFixed(2);
	if (me) me.textContent = m.toFixed(2);
	const eps = 0.01;
	const deficit = m - p;
	const balanceMsgBase = 'trade-offer-balance-msg mb-0';
	if (dl) {
		if (deficit > eps) {
			dl.textContent = 'You owe ' + deficit.toFixed(2) + ' gp';
			dl.className = balanceMsgBase + ' text-warning';
		} else if (deficit < -eps) {
			const overpay = Math.round(Math.abs(deficit) * 100) / 100;
			const fav = tradeFavorDeltaFromOverpayGp(overpay);
			const shortCu = (window.tradeState && window.tradeState.merchantChangeShortCu) || 0;
			const shortLine =
				shortCu > 0
					? '<span class="d-block text-warning mt-1">The merchant is out of coin to balance the trade.</span>'
					: '';
			dl.className = balanceMsgBase + ' text-info trade-offer-balance-msg--overpay';
			if (fav > 0) {
				dl.innerHTML =
					'<span class="d-block">Overpay by ' +
					overpay.toFixed(2) +
					' gp</span>' +
					'<span class="d-block text-success">+' +
					fav +
					' Favor</span>' +
					shortLine;
			} else if (shortLine) {
				dl.innerHTML =
					'<span class="d-block">Overpay by ' + overpay.toFixed(2) + ' gp</span>' + shortLine;
			} else {
				dl.textContent = 'Overpay by ' + overpay.toFixed(2) + ' gp';
			}
		} else {
			dl.className = balanceMsgBase + ' text-success';
			dl.textContent = 'Balanced';
		}
	}
	if (favorMsg) {
		favorMsg.textContent = '';
		favorMsg.classList.add('d-none');
	}
	if (accept) {
		// Trader never accepts being shorted; player may overpay (favor-weighted gp) to gain Favor.
		const playerMeetsTraderValue = p + eps >= m;
		const hasAny = window.tradeState.playerOffer.length > 0 || window.tradeState.merchantOffer.length > 0;
		const canAccept = playerMeetsTraderValue && hasAny;
		// Stay clickable so we can show displayWarning(); use opacity + aria for "disabled" affordance.
		accept.disabled = false;
		accept.classList.toggle('opacity-50', !canAccept);
		accept.style.cursor = canAccept ? '' : 'not-allowed';
		accept.setAttribute('aria-disabled', canAccept ? 'false' : 'true');
	}
}

const TRADE_COIN_ORDER = Object.entries(COIN_GP).map(([item_id, gp]) => ({ item_id, cu: Math.round(gp * 100) }))
	.sort((a, b) => b.cu - a.cu);

/**
 * One coin from the smallest denomination available whose face value >= needCu (copper units).
 * Covers "I only have gold" / can't make exact silver+copper change.
 */
function tradeRoundUpOnePlayerCoin(pool: CoinStack[], needCu: number): boolean {
	if (needCu <= 0 || !pool || !window.tradeState) return false;
	for (let i = TRADE_COIN_ORDER.length - 1; i >= 0; i--) {
		const denom = TRADE_COIN_ORDER[i];
		if (!denom) continue;
		if (denom.cu < needCu) continue;
		const stacks = pool.filter((s: CoinStack) => s.item_id === denom.item_id && s.qty > 0);
		if (stacks.length === 0) continue;
		const st = stacks[0];
		if (!st) continue;
		const unitGp = denom.cu / 100;
		const thumbUrl = String(st.thumb_url || st.thumbUrl || '').trim();
		const entry = {
			key: tradeRandomKey(),
			inventoryId: st.inventory_id,
			qty: 1,
			unitGp,
			name: st.name || 'Coin',
			tradeOwnerEcId: window.tradeState.playerEcId,
			isCoin: true,
			thumbUrl: thumbUrl || undefined,
			item_id: typeof st.item_id === 'string' ? st.item_id : undefined,
		};
		tradeMergeCoinOfferEntry('player', entry);
		st.qty -= 1;
		return true;
	}
	return false;
}

function tradeAddMerchantChangeCoins(mPool: CoinStack[], surplusCu: number): number {
	let left = surplusCu;
	if (left <= 0 || !mPool || !window.tradeState) return left;
	for (const denom of TRADE_COIN_ORDER) {
		const stacks = mPool.filter((s: CoinStack) => s.item_id === denom.item_id && s.qty > 0);
		for (const st of stacks) {
			while (left > 0 && st.qty > 0) {
				const maxTake = Math.min(st.qty, Math.floor(left / denom.cu));
				if (maxTake <= 0) break;
				const unitGp = denom.cu / 100;
				const thumbUrl = String(st.thumb_url || st.thumbUrl || '').trim();
				const entry = {
					key: tradeRandomKey(),
					inventoryId: st.inventory_id,
					qty: maxTake,
					unitGp,
					name: st.name || 'Coin',
					tradeOwnerEcId: window.tradeState.merchantEcId,
					isCoin: true,
					thumbUrl: thumbUrl || undefined,
					item_id: typeof st.item_id === 'string' ? st.item_id : undefined,
				};
				tradeMergeCoinOfferEntry('merchant', entry);
				st.qty -= maxTake;
				left -= maxTake * denom.cu;
			}
		}
	}
	return left;
}

function balanceTradeWithCoins() {
	if (!window.tradeState || !window.tradeState.active) return;
	window.tradeState.merchantChangeShortCu = 0;
	let p = 0;
	let m = 0;
	for (const o of window.tradeState.playerOffer) p += tradeLineTradeGp(o, 'player');
	for (const o of window.tradeState.merchantOffer) m += tradeLineTradeGp(o, 'merchant');
	p = Math.round(p * 100) / 100;
	m = Math.round(m * 100) / 100;
	let needCu = Math.max(0, Math.round((m - p) * 100 + 1e-6));

	if (needCu > 0) {
		const pool = window.tradeState.coinStacksAvailable || [];
		for (const denom of TRADE_COIN_ORDER) {
			const stacks = pool.filter((s: CoinStack) => s.item_id === denom.item_id && s.qty > 0);
			for (const st of stacks) {
				while (needCu > 0 && st.qty > 0) {
					const maxTake = Math.min(st.qty, Math.floor(needCu / denom.cu));
					if (maxTake <= 0) break;
					const unitGp = denom.cu / 100;
					const thumbUrl = String(st.thumb_url || st.thumbUrl || '').trim();
					const entry = {
						key: tradeRandomKey(),
						inventoryId: st.inventory_id,
						qty: maxTake,
						unitGp,
						name: st.name || 'Coin',
						tradeOwnerEcId: window.tradeState.playerEcId,
						isCoin: true,
						thumbUrl: thumbUrl || undefined,
						item_id: typeof st.item_id === 'string' ? st.item_id : undefined,
					};
					tradeMergeCoinOfferEntry('player', entry);
					st.qty -= maxTake;
					needCu -= maxTake * denom.cu;
				}
			}
		}
		if (needCu > 0 && tradeRoundUpOnePlayerCoin(pool, needCu)) {
			needCu = 0;
		}
		if (needCu > 0) {
			displayWarning('Not enough coins to balance (short by ~' + (needCu / 100).toFixed(2) + ' gp).');
		}
	}

	p = 0;
	m = 0;
	for (const o of window.tradeState.playerOffer) p += tradeLineTradeGp(o, 'player');
	for (const o of window.tradeState.merchantOffer) m += tradeLineTradeGp(o, 'merchant');
	p = Math.round(p * 100) / 100;
	m = Math.round(m * 100) / 100;
	let surplusCu = Math.max(0, Math.round((p - m) * 100 + 1e-6));
	if (surplusCu > 0) {
		const mPool = window.tradeState.merchantCoinStacksAvailable || [];
		const merchantChangeShortCu = tradeAddMerchantChangeCoins(mPool, surplusCu);
		if (merchantChangeShortCu > 0) {
			window.tradeState.merchantChangeShortCu = merchantChangeShortCu;
		}
	}
	tradeRenderAllStaged();
	calculateTradeBalance();
}

/** Raw favor correction; pricing remains a browser decision using the panel's campaign economy. */
function tradeAdoptSavedFavor(body: Record<string, unknown> | null): void {
    const st = window.tradeState, saved = body?.saved as Record<string, unknown> | undefined;
    if (!st || !saved || !Object.prototype.hasOwnProperty.call(saved, 'merchant_favor')) return;
    st.merchantFavor = saved.merchant_favor == null ? null : Number(saved.merchant_favor);
    st.favor = pricingFavor(st.merchantFavor, st.economy); Object.assign(st, favorPrices(st.favor));
    st.tierDescription = favorTier(st.favor);
    tradeApplyFavorUi(); tradeRenderAllStaged(); calculateTradeBalance();
}
function exchangeDecisionError(error: unknown): string {
    const row = error as { refusal?: string; reason?: string; message?: string } | null;
    return row?.refusal ?? row?.reason ?? row?.message ?? String(error);
}
async function acceptTrade() {
	if (!window.tradeState || !window.tradeState.active) return;
	const session = window.tradeState;
	if (!await exchangeReachGate(session.playerEcId, session.merchantEcId)) return;
	if (window.tradeState !== session || !session.active) return;

	const merchantEcId = window.tradeState.merchantEcId;
	const playerEcId = window.tradeState.playerEcId;
	const player_offer_json = JSON.stringify(
		window.tradeState.playerOffer.map((o: TradeEntry) => ({ inventory_id: o.inventoryId, qty: o.qty }))
	);
	const merchant_offer_json = JSON.stringify(
		window.tradeState.merchantOffer.map((o: TradeEntry) => ({ inventory_id: o.inventoryId, qty: o.qty }))
	);
	const encounter_id = tradeTrainerEncounterId();

	const warnEl = document.getElementById('div_sidelog_warning');
	const acceptBtn = document.querySelector<HTMLButtonElement>('#btn_trade_accept');
	const acceptBtnHtml = acceptBtn ? acceptBtn.innerHTML : '';
	const tradeSpinnerHtml =
		'<div class="d-flex justify-content-center align-items-center gap-2 p-3 text-muted small">' +
		'<span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>' +
		'<span>Completing trade…</span></div>';
	const acceptSpinnerOnly =
		'<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

	try {
		if (warnEl) warnEl.innerHTML = tradeSpinnerHtml;
		if (acceptBtn) {
			acceptBtn.disabled = true;
			acceptBtn.innerHTML = acceptSpinnerOnly;
		}

        const answer = await window.ftSubmitExchange({ kind: 'trade', encounterId: encounter_id, playerId: playerEcId, counterpartId: merchantEcId,
            merchantFavor: session.merchantFavor, economy: session.economy, playerOffer: JSON.parse(player_offer_json), merchantOffer: JSON.parse(merchant_offer_json) });
		if (warnEl) warnEl.innerHTML = '';
		if (!answer.ok) {
			if (answer.status === 409) tradeAdoptSavedFavor(answer.body);
			displayWarning(answer.status === 409 ? 'The trade changed. Review the items and prices, then accept again.'
				: exchangeOutcomeUnknown(answer) ? 'The trade could not be confirmed. Accepting the same trade again is safe — it will never trade twice.'
				: String(answer.error || 'Trade request failed.'));
			return;
		}
		if (typeof loadPostFunctions === 'function') {
			await loadPostFunctions(true);
		}
		exitTradeMode({ restoreMerchant: false });
		// Force player caster refresh as safety net (websocket may race DOM during exitTradeMode)
		if (playerEcId && typeof refreshCasterSheet === 'function') {
			refreshCasterSheet(playerEcId);
		}
		if (typeof openItemInfo === 'function' && merchantEcId) {
			await openItemInfo(merchantEcId, false);
		}
	} catch (err) {
		console.error('acceptTrade', err);
		displayWarning(exchangeDecisionError(err));
        if (warnEl) warnEl.innerHTML = '';
	} finally {
		if (acceptBtn && acceptBtn.isConnected) {
			acceptBtn.disabled = false;
			acceptBtn.innerHTML = acceptBtnHtml;
		}
	}
}

/**
 * Merchant portrait in trade panel: exit trade and restore sheet.
 * Ignores clicks on carousel prev/next and indicators so users can browse pictures.
 */
function tradeMerchantPortraitClick(ev?: MouseEvent) {
	if (ev?.target instanceof Element && ev.target.closest('button, .carousel-indicators')) {
		return;
	}
	exitTradeMode();
}

async function haggleMerchant() {
	const st = window.tradeState;
	if (!st || !st.active) return;
    if (st.hasHaggled) { displayWarning('You already haggled with this merchant this visit.'); return; }
	if (!await exchangeReachGate(st.playerEcId, st.merchantEcId)) return;
	if (window.tradeState !== st || !st.active) return;
	const btn = document.querySelector<HTMLButtonElement>('#btn_trade_haggle');
	if (btn && (btn.disabled || btn.classList.contains('disabled'))) return;

	const encounter_id = tradeTrainerEncounterId();
	const warnEl = document.getElementById('div_sidelog_warning');

	try {
        if (btn) btn.disabled = true;
        const answer = await window.ftSubmitExchange({ kind: 'haggle', encounterId: encounter_id, playerId: st.playerEcId, counterpartId: st.merchantEcId,
            merchantFavor: st.merchantFavor, economy: st.economy });
		if (answer.status === 409) tradeAdoptSavedFavor(answer.body);
		const data = { ...(answer.body ?? {}), ok: answer.ok, error: answer.status === 409 ? "The trader's favor changed — the panel now shows it. Haggle again." : answer.error } as Record<string, any>;
		if (!data.ok) {
			if (typeof displayWarning === 'function') {
				displayWarning(data.error || 'Haggle failed.');
			} else if (warnEl) {
				warnEl.innerHTML =
					'<h5 class="bg-warning text-dark p-2">' + escapeHtml(data.error || 'Haggle failed.') + '</h5>';
			}
			return;
		}
		st.merchantFavor = data.merchant_favor == null ? null : Number(data.merchant_favor);
        st.favor = typeof data.favor === 'number' ? data.favor : st.favor;
		st.buyP = typeof data.buyP === 'number' ? data.buyP : st.buyP;
		st.sellP = typeof data.sellP === 'number' ? data.sellP : st.sellP;
		st.hasHaggled = true;
		st.tierDescription = typeof data.tierDescription === 'string' ? data.tierDescription : st.tierDescription;
		tradeApplyFavorUi();
		tradeRenderAllStaged();
		calculateTradeBalance();
		if (warnEl && data.messageHtml) {
			warnEl.innerHTML = data.messageHtml;
		}
	} catch (err) {
		console.error('haggleMerchant', err);
		displayWarning(exchangeDecisionError(err));
    } finally { if (btn) btn.disabled = st.hasHaggled; }
}

window.enterTradeMode = enterTradeMode;
window.exitTradeMode = exitTradeMode;
window.tradeMerchantPortraitClick = tradeMerchantPortraitClick;
window.balanceTradeWithCoins = balanceTradeWithCoins;
window.calculateTradeBalance = calculateTradeBalance;
window.tradeClearAllOffers = tradeClearAllOffers;
window.tradeAddWaresToOffer = tradeAddWaresToOffer;
window.acceptTrade = acceptTrade;
window.haggleMerchant = haggleMerchant;
// Required by extraction: template.js drag/drop handlers (DragManager dragstart/drop/dragend)
// call these as plain identifiers; before extraction they worked because everything shared
// template.js's top-level scope. Exposing on window keeps those call sites working.
window.tradeDropzoneSetDragHint = tradeDropzoneSetDragHint;
window.tradeDropzoneClearDragHint = tradeDropzoneClearDragHint;
window.tradeCommitDropFromDrag = tradeCommitDropFromDrag;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TRAINER (skill training — pay GP, Training status, finish_training roll)
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

window.trainState = window.trainState || null;

async function enterTrainMode(trainerEcId: string, playerEcId: string, partnerAlsoMerchant?: boolean) {
	if (!trainerEcId || !playerEcId) {
		displayWarning('Missing training participants.');
		return;
	}
	if (!await exchangeParticipantGate(playerEcId, trainerEcId, true)) {
		exchangeInvalidateSession(); window.trainState = null;
		return;
	}
	if (!await exchangeReachGate(playerEcId, trainerEcId)) return;
	if (window.tradeState && window.tradeState.active && typeof exitTradeMode === 'function') {
		exitTradeMode({ restoreMerchant: false });
	}
	// New panel session — see enterTradeMode.
	exchangeInvalidateSession();
	window.trainState = null;
	window.__sideinfoPartnerContext = {
		partnerEcId: trainerEcId,
		playerEcId: playerEcId,
		hasMerchant: !!partnerAlsoMerchant,
		hasTrainer: true,
	};
	if (typeof forceOpenSideInfoPanel === 'function') {
		forceOpenSideInfoPanel();
	}
	document.body.classList.add('train-mode-active');
	tradeTryActivatePlayerInventoryTab(playerEcId);

	const campaign_id = tradeTrainerCampaignId();
	const encounter_id = tradeTrainerEncounterId();
	const caster_id = tradeTrainerActiveCasterId();

	clearSideInfoTrainerPanelContent();
	await queryData(
		BASE_URL + 'options/tools/inventory/trade/load_train_panel_inc.php',
		'div_sideinfo_trainer_panel',
		{
			...await exchangeParticipantPresentations(playerEcId, trainerEcId),
			campaign_id,
			encounter_id,
			caster_id,
			trainer_ec_id: trainerEcId,
			player_ec_id: playerEcId,
		}
	);

	if (!document.getElementById('div_trainer_train_panel')) {
		document.body.classList.remove('train-mode-active');
		displayWarning('Could not open training panel.');
		clearSideInfoTrainerPanelContent();
		sideInfoHideExchangeTabUi();
		sideInfoActivateItemTab();
		window.__sideinfoPartnerContext = null;
		if (trainerEcId && typeof openItemInfo === 'function') {
			openItemInfo(trainerEcId, false);
		}
		return;
	}

	sideInfoShowExchangeTabUi('train');
	sideInfoActivateTrainerTab();
	await trainInitStateAfterPanel(trainerEcId, playerEcId);
	if (typeof scheduleSideinfoExchangeReachCheck === 'function') {
		scheduleSideinfoExchangeReachCheck();
	}
}

// =============================================================================
// TRAINER SUBSYSTEM (trainer UI)
// Mirror of TRADE SUBSYSTEM above for trainer interactions —
// state init, render-staged, coin balancing, offer merging.
// =============================================================================
async function trainInitStateAfterPanel(trainerEcId: string, playerEcId: string) {
	const favorPayload = await exchangeLoadPanelFacts('div_trainer_train_panel', playerEcId, trainerEcId);
	if (!favorPayload) return;
	const playerCoinPool = favorPayload.playerCoins;
	window.trainState = {
		active: true,
		trainerEcId,
		playerEcId,
		playerOffer: [],
		coinStacksAvailable: playerCoinPool.map((c) => ({ ...c })),
		coinStacksInitialSnapshot: JSON.parse(JSON.stringify(playerCoinPool)),
		favor: favorPayload.favor,
        merchantFavor: favorPayload.rawFavor, economy: favorPayload.economy,
		sellP: favorPayload.sellP,
		tierDescription: favorPayload.tierDescription,
		selectedSkillVar: '',
		trainingCostGp: 0, skillVerdicts: favorPayload.training,
	};
    const select = document.querySelector<HTMLSelectElement>('#train_skill_select');
    if (select) {
        for (const option of [...select.options]) if (option.value) option.remove();
        const skills = Object.entries(favorPayload.training).filter(([, verdict]) => verdict.trainerTier > 0 && verdict.skillId)
            .sort(([, a], [, b]) => a.skillName.localeCompare(b.skillName));
        for (const [variable, verdict] of skills) {
            const option = document.createElement('option'); option.value = variable;
            option.textContent = `${verdict.skillName} (T${verdict.trainerTier}, ${verdict.cost} gp)`;
            option.disabled = !verdict.canTrain; select.appendChild(option);
        }
    }
	trainApplyFavorUi();
	trainRenderStaged();
	trainCalculateBalance();

	if (typeof refreshDragAndDrop === 'function') {
		refreshDragAndDrop();
	}
	if (typeof fateTideRefreshInventoryUnequippedSearch === 'function') {
		fateTideRefreshInventoryUnequippedSearch();
	}
	const trainStockHost = document.getElementById('div_trainer_train_panel');
	if (
		trainStockHost
		&& typeof fateTideScrubInventoryUnequippedSearchInputsInContainer === 'function'
		&& typeof fateTideRefreshInventoryUnequippedSearch === 'function'
	) {
		fateTideScrubInventoryUnequippedSearchInputsInContainer(trainStockHost);
		fateTideRefreshInventoryUnequippedSearch();
	}
}

function trainApplyFavorUi() {
	const st = window.trainState;
	if (!st || !st.active) return;
	exchangeApplyFavorDisplay('train_merchant_favor_emoji', st.favor, st.tierDescription);
}

function exitTrainMode(options?: { restoreTrainer?: boolean }) {
	const restoreTrainer = !options || options.restoreTrainer !== false;
	const trainerEcId = window.trainState?.trainerEcId;
	exchangeInvalidateSession();
	document.body.classList.remove('train-mode-active');
	window.trainState = null;
	clearSideInfoTrainerPanelContent();
	sideInfoHideExchangeTabUi();
	sideInfoActivateItemTab();
	window.__sideinfoPartnerContext = null;
	if (restoreTrainer && trainerEcId && typeof openItemInfo === 'function') {
		openItemInfo(trainerEcId, false);
	}
}

function trainOnSkillSelectChange(skillVar: string) {
	const st = window.trainState;
	if (!st || !st.active) return;
	const verdict = st.skillVerdicts[skillVar];
    const can = verdict?.canTrain === true;
    const cost = verdict?.cost ?? 0;
	st.selectedSkillVar = skillVar || '';
	st.trainingCostGp = Number.isFinite(cost) ? cost : 0;
	const hint = document.getElementById('train_skill_hint');
	if (hint) {
		if (!skillVar) {
			hint.textContent = '';
		} else if (!can) {
			hint.textContent = 'You cannot train this skill with this trainer at your current level.';
		} else {
			hint.textContent = 'Pay the listed cost in goods or coins, then Begin training.';
		}
	}
	trainCalculateBalance();
}

function trainQtyStaged(inventoryId: string): number {
	const st = window.trainState;
	if (!st) return 0;
	return st.playerOffer.filter((o: TradeEntry) => o.inventoryId === inventoryId).reduce((s: number, o: TradeEntry) => s + o.qty, 0);
}

function trainMaxAddQtyFromEl(el: HTMLElement): number {
	const cap = exchangeStockQuantity(el);
	const already = trainQtyStaged(el.dataset.inventory_id ?? '');
	return Math.max(0, cap - already);
}

function trainCommitDropFromDrag(el: HTMLElement, trainZone: HTMLElement) {
	if (!window.trainState || !window.trainState.active) return;
	const expectedOwner = window.trainState.playerEcId;
	const owner = el.dataset.trade_owner_ec_id || '';
	if (!owner || owner !== expectedOwner) {
		displayWarning('That item cannot go in your training payment.');
		return;
	}
	const maxAdd = trainMaxAddQtyFromEl(el);
	if (maxAdd <= 0) {
		displayWarning('No remaining quantity of that stack to add.');
		return;
	}
	const id = el.dataset.inventory_id || '';
	if (!id) {
		displayWarning('That item has no inventory identifier.');
		return;
	}
	const fromId = el.dataset.move_from_inventory_id || null;
	const showLog = parseInt(el.dataset.show_log_message || '1', 10);
	if (maxAdd > 1) {
		showInventorySliderPopup(
			id,
			maxAdd,
			fromId,
			'',
			'move_inventory',
			showLog,
			false,
			null,
			null,
			0,
			0,
			0,
			(qty: number) => trainAddStagedFromElement(el, qty)
		);
	} else {
		trainAddStagedFromElement(el, 1);
	}
}

function trainAddStagedFromElement(el: HTMLElement, qty: number) {
	if (!window.trainState || !window.trainState.active) return;
	const maxAdd = trainMaxAddQtyFromEl(el);
	if (qty > maxAdd) qty = maxAdd;
	if (qty <= 0) return;
	const inventoryId = el.dataset.inventory_id || '';
	if (!inventoryId) return;
	let unitGp = parseFloat(el.dataset.cost_gp ?? '');
	if (!Number.isFinite(unitGp)) unitGp = 0;
	const isCoin = tradeIsCoinItemId(el.dataset.item_id ?? '');
	const imgEl = el.querySelector<HTMLImageElement>('img.img-thumbnail, img.token-img, .d-flex img');
	const thumbUrl =
		imgEl && (imgEl.currentSrc || imgEl.src)
			? String(imgEl.currentSrc || imgEl.src).trim()
			: '';
	const itemId = el.dataset.item_id || '';
	const entry = {
		key: tradeRandomKey(),
		inventoryId,
		qty,
		unitGp,
		name: el.dataset.inventory_name || 'Item',
		tradeOwnerEcId: el.dataset.trade_owner_ec_id || '',
		isCoin,
		thumbUrl,
		item_id: itemId || undefined,
		sizeVar: el.dataset.size || 'tiny',
		itemType: el.dataset.item_type || '',
		enchantmentTemplateId: el.dataset.enchantment_template_id || '',
	};
	if (isCoin) {
		tradeMergeCoinOfferEntryTrain(entry);
		trainApplyCoinPoolDelta(inventoryId, -qty);
	} else {
		window.trainState.playerOffer.push(entry);
	}
	trainRenderStaged();
	trainCalculateBalance();
}

function trainApplyCoinPoolDelta(inventoryId: string, deltaQty: number) {
	const st = window.trainState;
	if (!st || !st.coinStacksAvailable) return;
	for (const row of st.coinStacksAvailable) {
		if (row.inventory_id === inventoryId) {
			row.qty = Math.max(0, (row.qty || 0) + deltaQty);
			break;
		}
	}
}

function tradeMergeCoinOfferEntryTrain(entry: TradeEntry) {
	if (!window.trainState || !entry || !entry.isCoin) return;
	const arr = window.trainState.playerOffer;
	const existing = arr.find((e: TradeEntry) => e.isCoin && e.inventoryId === entry.inventoryId);
	if (existing) {
		existing.qty += entry.qty;
		if (!existing.thumbUrl && entry.thumbUrl) existing.thumbUrl = entry.thumbUrl;
		if (existing.item_id == null && entry.item_id != null) existing.item_id = entry.item_id;
		return;
	}
	arr.push(entry);
}

function trainLineGp(entry: TradeEntry): number {
	const st = window.trainState;
	if (!st || !entry) return 0;
	const mult = entry.isCoin ? 1 : st.sellP;
	return entry.unitGp * entry.qty * mult;
}

function trainRenderStaged() {
	const box = document.getElementById('train_zone_player_offer');
	if (!box || !window.trainState) return;
	box.innerHTML = '';
	const thumbW = 72;
	for (const entry of window.trainState.playerOffer) {
		const wrap = document.createElement('div');
		wrap.className = 'trade-staged-item';
		wrap.dataset.stagedKey = entry.key;
		const gp = trainLineGp(entry).toFixed(2);
		const inventoryLike = tradeEntryToInventoryLike(entry);
		let thumbHtml = '';
		try {
			thumbHtml = renderInventoryThumbnail(inventoryLike, {}, {
				show_overlay: false,
				use_size_based_width: false,
				thumbnail_width: thumbW,
				thumbnail_height: null,
				is_draggable: false,
				show_log_message: 0,
				show_icons: true,
				additional_classes: ['trade-staged-inventory-thumb'],
				dom_id: false,
				inline_layout: true,
				exclude_dropzone: true,
				always_show_quantity_badge: true,
				override_thumb_url: entry.thumbUrl || null,
				omit_tooltip: true,
			});
		} catch (e) {
			thumbHtml = '<div class="small text-muted text-center p-1" style="width:' + thumbW + 'px;">?</div>';
		}
		wrap.innerHTML =
			'<div class="trade-staged-thumb-stack position-relative d-inline-block">' +
			thumbHtml +
			'<button type="button" class="trade-remove-staged" aria-label="Remove">' +
			'<svg class="trade-remove-staged-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="12" height="12"><path fill="none" stroke="currentColor" stroke-width="2" d="M3 3l6 6M9 3l-6 6"/></svg>' +
			'</button></div>' +
			'<div class="trade-staged-gp text-center"><span class="badge bg-dark border border-secondary text-light fw-normal">' +
			gp +
			' gp</span></div>';
		wrap.querySelector('.trade-remove-staged')?.addEventListener('click', (ev) => {
			ev.stopPropagation();
			trainRemoveStaged(entry.key);
		});
		box.appendChild(wrap);
	}
}

function trainRemoveStaged(key: string) {
	const st = window.trainState;
	if (!st) return;
	const idx = st.playerOffer.findIndex((o: TradeEntry) => o.key === key);
	if (idx < 0) return;
	const removed = st.playerOffer[idx];
	st.playerOffer.splice(idx, 1);
	if (!removed) return;
	if (removed.isCoin) {
		trainApplyCoinPoolDelta(removed.inventoryId, removed.qty);
	}
	trainRenderStaged();
	trainCalculateBalance();
}

function trainClearOffers() {
	const st = window.trainState;
	if (!st || !st.active) return;
	st.playerOffer = [];
	if (Array.isArray(st.coinStacksInitialSnapshot)) {
		st.coinStacksAvailable = JSON.parse(JSON.stringify(st.coinStacksInitialSnapshot));
	}
	trainRenderStaged();
	trainCalculateBalance();
}

function trainCalculateBalance() {
	const st = window.trainState;
	if (!st || !st.active) return;
	let p = 0;
	for (const o of st.playerOffer) p += trainLineGp(o);
	p = Math.round(p * 100) / 100;
	const cost = Math.round((st.trainingCostGp || 0) * 100) / 100;
	const pe = document.getElementById('train_val_player');
	const se = document.getElementById('train_val_service');
	const dl = document.getElementById('train_balance_label');
	const btn = document.querySelector<HTMLButtonElement>('#btn_train_begin');
	if (pe) pe.textContent = p.toFixed(2);
	if (se) se.textContent = String(cost);
	const eps = 0.01;
	const deficit = cost - p;
	const hasSkill = !!st.selectedSkillVar;
	if (dl) {
		if (!hasSkill || cost <= 0) {
			dl.textContent = 'Select a skill to train.';
			dl.className = 'text-center text-muted';
		} else if (deficit > eps) {
			dl.textContent = 'Short by ' + deficit.toFixed(2) + ' gp';
			dl.className = 'text-center text-warning';
		} else if (deficit < -eps) {
			const overpay = Math.abs(deficit);
			const fav = tradeFavorDeltaFromOverpayGpTrain(overpay);
			dl.className = 'text-center text-info';
			if (fav > 0) {
				dl.innerHTML =
					'<span class="d-block">Overpay by ' +
					overpay.toFixed(2) +
					' gp</span><span class="d-block text-success">+' +
					fav +
					' Favor</span>';
			} else {
				dl.textContent = 'Overpay by ' + overpay.toFixed(2) + ' gp';
			}
		} else {
			dl.textContent = 'Balanced';
			dl.className = 'text-center text-success';
		}
	}
	const canTrain = st.skillVerdicts[st.selectedSkillVar]?.canTrain === true;
	const canBegin = hasSkill && canTrain && cost > 0 && p + eps >= cost && st.playerOffer.length > 0;
	if (btn) {
		btn.disabled = false;
		btn.classList.toggle('opacity-50', !canBegin);
		btn.style.cursor = canBegin ? '' : 'not-allowed';
		btn.setAttribute('aria-disabled', canBegin ? 'false' : 'true');
	}
}

function tradeFavorDeltaFromOverpayGpTrain(overpayGp: number): number {
    return window.trainState ? giftFavorDelta(overpayGp, window.trainState.merchantFavor) : 0;
}

function trainRoundUpOneCoin(pool: CoinStack[], needCu: number): boolean {
	if (needCu <= 0 || !pool || !window.trainState) return false;
	for (let i = TRADE_COIN_ORDER.length - 1; i >= 0; i--) {
		const denom = TRADE_COIN_ORDER[i];
		if (!denom) continue;
		if (denom.cu < needCu) continue;
		const stacks = pool.filter((s: CoinStack) => s.item_id === denom.item_id && s.qty > 0);
		if (stacks.length === 0) continue;
		const row = stacks[0];
		if (!row) continue;
		const unitGp = denom.cu / 100;
		const thumbUrl = String(row.thumb_url || row.thumbUrl || '').trim();
		const entry = {
			key: tradeRandomKey(),
			inventoryId: row.inventory_id,
			qty: 1,
			unitGp,
			name: row.name || 'Coin',
			tradeOwnerEcId: window.trainState.playerEcId,
			isCoin: true,
			thumbUrl: thumbUrl || undefined,
			item_id: typeof row.item_id === 'string' ? row.item_id : undefined,
		};
		tradeMergeCoinOfferEntryTrain(entry);
		row.qty -= 1;
		return true;
	}
	return false;
}

function balanceTrainWithCoins() {
	const st = window.trainState;
	if (!st || !st.active) return;
	const cost = st.trainingCostGp || 0;
	let p = 0;
	for (const o of st.playerOffer) p += trainLineGp(o);
	p = Math.round(p * 100) / 100;
	let needCu = Math.max(0, Math.round((cost - p) * 100 + 1e-6));
	if (needCu > 0) {
		const pool = st.coinStacksAvailable || [];
		for (const denom of TRADE_COIN_ORDER) {
			const stacks = pool.filter((s: CoinStack) => s.item_id === denom.item_id && s.qty > 0);
			for (const row of stacks) {
				while (needCu > 0 && row.qty > 0) {
					const maxTake = Math.min(row.qty, Math.floor(needCu / denom.cu));
					if (maxTake <= 0) break;
					const unitGp = denom.cu / 100;
					const thumbUrl = String(row.thumb_url || row.thumbUrl || '').trim();
					const entry = {
						key: tradeRandomKey(),
						inventoryId: row.inventory_id,
						qty: maxTake,
						unitGp,
						name: row.name || 'Coin',
						tradeOwnerEcId: st.playerEcId,
						isCoin: true,
						thumbUrl: thumbUrl || undefined,
						item_id: typeof row.item_id === 'string' ? row.item_id : undefined,
					};
					tradeMergeCoinOfferEntryTrain(entry);
					row.qty -= maxTake;
					needCu -= maxTake * denom.cu;
				}
			}
		}
		if (needCu > 0) {
			trainRoundUpOneCoin(pool, needCu);
		}
		if (needCu > 0) {
			p = 0;
			for (const o of st.playerOffer) p += trainLineGp(o);
			p = Math.round(p * 100) / 100;
			needCu = Math.max(0, Math.round((cost - p) * 100 + 1e-6));
		}
		if (needCu > 0) {
			displayWarning('Not enough coins to balance (short by ~' + (needCu / 100).toFixed(2) + ' gp).');
		}
	}
	trainRenderStaged();
	trainCalculateBalance();
}

async function acceptTrainAndBegin() {
	const st = window.trainState;
	if (!st || !st.active) return;
	if (!await exchangeReachGate(st.playerEcId, st.trainerEcId)) return;
	if (window.trainState !== st || !st.active) return;
	const trainerEcId = st.trainerEcId;
	const playerEcId = st.playerEcId;
	const player_offer_json = JSON.stringify(
		st.playerOffer.map((o: TradeEntry) => ({ inventory_id: o.inventoryId, qty: o.qty }))
	);
	const encounter_id = tradeTrainerEncounterId();
	const warnEl = document.getElementById('div_sidelog_warning');
	const btn = document.querySelector<HTMLButtonElement>('#btn_train_begin');
	const btnHtml = btn ? btn.innerHTML : '';
	const spin =
		'<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
	try {
		if (warnEl) {
			warnEl.innerHTML =
				'<div class="d-flex justify-content-center align-items-center gap-2 p-3 text-muted small"><span class="spinner-border spinner-border-sm text-primary"></span><span>Starting training…</span></div>';
		}
		if (btn) {
			btn.disabled = true;
			btn.innerHTML = spin;
		}
        const answer = await window.ftSubmitExchange({ kind: 'training', encounterId: encounter_id, playerId: playerEcId, counterpartId: trainerEcId,
            merchantFavor: st.merchantFavor, economy: st.economy, playerOffer: JSON.parse(player_offer_json), skillVar: st.selectedSkillVar });
		if (warnEl) warnEl.innerHTML = '';
		if (!answer.ok) {
			if (answer.status === 409) {
                const saved = answer.body?.saved as Record<string, unknown> | undefined;
                if (saved && Object.prototype.hasOwnProperty.call(saved, 'merchant_favor')) {
                    st.merchantFavor = saved.merchant_favor == null ? null : Number(saved.merchant_favor);
                    st.favor = pricingFavor(st.merchantFavor, st.economy); st.sellP = favorPrices(st.favor).sellP;
                    st.tierDescription = favorTier(st.favor); trainApplyFavorUi(); trainRenderStaged(); trainCalculateBalance();
                }
			}
			displayWarning(answer.status === 409 ? 'The training details changed. Review the payment and begin again.'
				: exchangeOutcomeUnknown(answer) ? 'Training could not be confirmed. Beginning the same training again is safe — it will never charge twice.'
				: String(answer.error || 'Training request failed.'));
			return;
		}
		if (typeof loadPostFunctions === 'function') {
			await loadPostFunctions(true);
		}
		exitTrainMode({ restoreTrainer: false });
		if (playerEcId && typeof refreshCasterSheet === 'function') {
			refreshCasterSheet(playerEcId);
		}
		if (typeof openItemInfo === 'function' && trainerEcId) {
			await openItemInfo(trainerEcId, false);
		}
	} catch (err) {
		console.error('acceptTrainAndBegin', err);
		displayWarning(exchangeDecisionError(err));
        if (warnEl) warnEl.innerHTML = '';
	} finally {
		if (btn && btn.isConnected) {
			btn.disabled = false;
			btn.innerHTML = btnHtml;
		}
	}
}

function trainTrainerPortraitClick(ev?: MouseEvent) {
	if (ev?.target instanceof Element && ev.target.closest('button, .carousel-indicators')) {
		return;
	}
	exitTrainMode();
}

// Live-stock glue for the websocket bundle (see LIVE STOCK REFRESH). Attached here so
// the WS side only ever optional-chains a window global and owns no trade policy.
window.ftExchangeOnParticipantUpdate = ftExchangeOnParticipantUpdate;
window.ftExchangeFavorChanged = () => { tradeApplyFavorUi(); trainApplyFavorUi(); calculateTradeBalance(); trainCalculateBalance(); };

window.enterTrainMode = enterTrainMode;
window.exitTrainMode = exitTrainMode;
window.trainOnSkillSelectChange = trainOnSkillSelectChange;
window.trainClearOffers = trainClearOffers;
window.balanceTrainWithCoins = balanceTrainWithCoins;
window.acceptTrainAndBegin = acceptTrainAndBegin;
window.trainTrainerPortraitClick = trainTrainerPortraitClick;
// Required by extraction: template.js DragManager drop handler calls this as a plain
// identifier when train mode is active; before extraction it worked because everything
// shared template.js's top-level scope.
window.trainCommitDropFromDrag = trainCommitDropFromDrag;


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// GM / DEV QUICK-OPEN — trade / train without the in-reach map gate
// -------------------------------------------------------------------------------------------------------------------------------------
// The player console buttons (creature_console_inc.php) only enable when the buyer PC is physically
// WITHIN REACH of the merchant/trainer on the map (is_within_reach footprint geometry). For a GM running
// a shop — or anyone driving a party member via "Act as this creature" — that positioning is pure friction.
// These open the panel directly, using the CURRENTLY CONTROLLED caster (ftGetActiveCasterId) as the
// buyer/student. They are emitted ONLY inside the GM-gated console strip (creature-console.ts renderGmControls,
// which renders only with player/campaign permission), so this is not a way for a plain player to skip reach.

function ftGmResolveActiveBuyer(partnerEcId: string): string {
	const buyer = tradeTrainerActiveCasterId();
	if (!buyer) {
		displayWarning('Take control of a character first ("Act as this creature"), then open trade / training.');
		return '';
	}
	if (partnerEcId && buyer.toUpperCase() === String(partnerEcId).toUpperCase()) {
		displayWarning('You are currently acting AS this merchant/trainer — switch to the buying character first.');
		return '';
	}
	return buyer;
}

// Read a boolean-ish layer flag off the partner's cached blob, to set the dual merchant+trainer
// affordance flag correctly (so a merchant who is ALSO a trainer offers the in-panel switch).
function ftPartnerLayerTruthy(partnerEcId: string, layerName: string): boolean {
	try {
		const partner = window.FateTide?.casterCache?.get(partnerEcId);
		if (!partner) {
			return false;
		}
		const v = window.ftLayerValue?.(partner, layerName) ?? null;
		const u = Array.isArray(v) ? v[0] : v;
		return u !== null && u !== undefined && u !== '' && u !== '0' && u !== 0 && u !== false;
	} catch (_e) {
		return false;
	}
}

function ftGmOpenTrade(merchantEcId: string) {
	const id = String(merchantEcId || '').trim();
	if (!id) {
		return;
	}
	const buyer = ftGmResolveActiveBuyer(id);
	if (!buyer) {
		return;
	}
	enterTradeMode(id, buyer, ftPartnerLayerTruthy(id, 'trainer'));
}

function ftGmOpenTrain(trainerEcId: string) {
	const id = String(trainerEcId || '').trim();
	if (!id) {
		return;
	}
	const buyer = ftGmResolveActiveBuyer(id);
	if (!buyer) {
		return;
	}
	enterTrainMode(id, buyer, ftPartnerLayerTruthy(id, 'merchant'));
}

window.ftGmOpenTrade = ftGmOpenTrade;
window.ftGmOpenTrain = ftGmOpenTrain;


export {
	ftGmResolveActiveBuyer,
	ftPartnerLayerTruthy,
	tradeEntryToInventoryLike,
	favorEmoji as tradeFavorEmojiFromValue,
	tradeGiftGpCostForNextPoint,
	tradeInventorySizeIdFromVariableName,
	tradeTrainerActiveCasterId,
	tradeTrainerCampaignId,
	tradeTrainerContext,
	tradeTrainerEncounterId,
};
